import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Plus, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DemoAccountCard } from './DemoAccountCard';
import { CreateDemoAccountModal } from './CreateDemoAccountModal';
import { AddFundsModal } from './AddFundsModal';
import { StrategyDeploymentModal } from './StrategyDeploymentModal';

interface DemoAccount {
  id: string;
  account_name: string;
  balance: number;
  initial_balance: number;
  equity: number;
  margin_used: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export const DemoAccountsManager = () => {
  const [accounts, setAccounts] = useState<DemoAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addFundsModalOpen, setAddFundsModalOpen] = useState(false);
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const { toast } = useToast();

  const fetchAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('demo_accounts')
        .select('id, account_name, balance, initial_balance, equity, margin_used, currency, created_at, updated_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching accounts:', error);
        return;
      }

      setAccounts(data ?? []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleReset = async (accountId: string) => {
    try {
      const { error } = await supabase.functions.invoke('demo-accounts', {
        method: 'PUT',
        body: { account_id: accountId, action: 'reset' },
      });

      if (error) throw new Error(error.message || 'Failed to reset account');

      toast({ title: 'Success', description: 'Demo account reset successfully' });
      fetchAccounts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to reset account',
        variant: 'destructive',
      });
    }
  };

  const handleAddFunds = (accountId: string) => {
    setSelectedAccountId(accountId);
    setAddFundsModalOpen(true);
  };

  const handleManageStrategies = (accountId: string) => {
    setSelectedAccountId(accountId);
    setStrategyModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient">Demo Accounts</h2>
          <p className="text-muted-foreground">
            Manage your virtual trading accounts and deploy strategies
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Account
        </Button>
      </div>

      {accounts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <CardTitle>No Demo Accounts</CardTitle>
                <CardDescription className="mt-2">
                  Create your first demo account to start testing trading strategies
                </CardDescription>
              </div>
              <Button onClick={() => setCreateModalOpen(true)}>
                Create Your First Account
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((account) => (
            <DemoAccountCard
              key={account.id}
              account={account}
              onReset={handleReset}
              onAddFunds={handleAddFunds}
              onManageStrategies={handleManageStrategies}
            />
          ))}
        </div>
      )}

      <CreateDemoAccountModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onAccountCreated={fetchAccounts}
      />

      <AddFundsModal
        open={addFundsModalOpen}
        onOpenChange={setAddFundsModalOpen}
        accountId={selectedAccountId}
        onFundsAdded={fetchAccounts}
      />

      <StrategyDeploymentModal
        open={strategyModalOpen}
        onOpenChange={setStrategyModalOpen}
        accountId={selectedAccountId}
      />
    </div>
  );
};